import React, { ChangeEvent, useEffect, useState, type JSX } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import * as queries from '../graphql/queries';
import { makeGraphqlQuery } from '../graphql/makeGraphqlQuery';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Unterzeichner } from '../shared/constants';
import { formatDDMMYYYY } from '../shared/dateTime';
import { HonorarEintrag, Mandant, Vorgang } from '../types';
import { Signature } from './common/Signature';
import { SPACING_BETWEEN_BOXES, SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { table } from './modal/HonorartabelleModal';
import { Euro } from './common/Euro';
import { MEHRWERTSTEUER } from '../shared/domain/vorgang';
import { anredeAuswahl } from '../frontendConstants';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function AuftragZurErstellungGutachten({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.auftragZurErstellungGutachten}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ auftragZurErstellungGutachten: event.target.checked })}
              name="auftragZurErstellungGutachten"
              color="primary"
              data-testid="auftragZurErstellungGutachtenCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="auftragZurErstellungGutachtenLink"
              >
                Auftrag zur Erstellung eines Schadengutachtens
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <AuftragModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function AuftragModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const [honorartabelle, setHonorartabelle] = useState<HonorarEintrag[]>([]);

  useEffect(() => {
    if (vorgang.mandant !== '') {
      makeGraphqlQuery(queries.holeMandant, {
        mandant: vorgang.mandant
      }).then((mandant: Mandant) => {
        if (mandant.honorartabelle) {
          setHonorartabelle(mandant.honorartabelle);
        }
      });
    }
  }, [vorgang.mandant]);

  const middleIndex = Math.ceil(honorartabelle.length / 2);
  const firstHalf = honorartabelle.splice(0, middleIndex);
  const secondHalf = honorartabelle.splice(-middleIndex);

  const url = useDateiUrl(vorgang?.unterschriften?.auftragZurErstellungGutachten?.key);

  const signDate = vorgang?.unterschriften?.auftragZurErstellungGutachten?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Auftrag zur Erstellung eines Schadengutachtens"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="auftragModal">
          <Grid size={12}>
            <Grid container>
              <Grid size={4}>
                Zum Gutachten
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="vorgangsnummer">
                {vorgang.vorgangsnummer}
              </Grid>
              <Grid size={4}>
                Schadensereignis vom
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="unfalldatum">
                {formatDDMMYYYY(vorgang?.unfalldaten?.datum)}
              </Grid>
              <Grid size={4}>
                Name des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="nameAuftraggeber">
                {anredeAuswahl.find((anrede) => anrede.value === vorgang.auftraggeber?.anrede)?.label} {vorgang.auftraggeber?.vorname}{' '}
                {vorgang.auftraggeber?.nachname}
              </Grid>
              <Grid size={4}>
                Anschrift des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="anschriftAuftraggeber">
                {vorgang?.auftraggeber?.strasse} {vorgang?.auftraggeber?.plz} {vorgang?.auftraggeber?.ort}
              </Grid>
              <Grid size={4}>
                Kennzeichen des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="kennzeichenAuftraggeber">
                {vorgang.fahrzeugdaten.kennzeichen}
              </Grid>
              <Grid size={4}>
                Kennzeichen des Unfallgegners
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="kennzeichenUnfallgegener">
                {vorgang?.unfalldaten?.kennzeichen}
              </Grid>
              <Grid size={4}>
                Name des Auftragnehmers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="nameAuftragnehmer">
                Experts24 Tassone GmbH, Hildener Str. 28, 42697 Solingen
              </Grid>
              <Grid size={4}>
                Fahrzeug steht im Betriebsvermögen
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="betriebsvermoegen">
                {vorgang?.auftraggeberVorsteuerabzugsberechtigt ? 'Ja' : 'Nein'}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§1 Geltung der Bedingungen für Schadensgutachten</Typography>
            <Typography variant="body2">
              Die Erstellung des Gutachtens durch den Auftragnehmer (AN) für den Auftraggeber (AG) erfolgt ausschließlich aufgrund dieser Geschäftsbedingungen.
              Sie gelten ausdrücklich nicht für Nachbesichtigungen, Reparaturbestätigungen oder Stellungnahmen des AN auf Prüfberichte.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§2 Auftragserteilung</Typography>
            <Typography variant="body2">
              Der Auftrag zur Gutachtenerstellung erfolgt regelmäßig durch (auch digitale) Unterschrift des AG unter dem Auftragsformular und Zugang des
              unterschriebenen Auftragsformulars beim AN.
            </Typography>
            <Typography variant="body2">
              Der Auftrag kann alternativ auch im Wege der elektronischen Auftragserteilung zustande kommen. In diesem Fall gibt der AG ein verbindliches
              Angebot ab, indem er
            </Typography>
            <Typography variant="body2">
              - die im Beauftragungsprozess bereitgestellten Vertragsbedingungen durch Anklicken der hierfür vorgesehenen Check-Boxen akzeptiert und
            </Typography>
            <Typography variant="body2">
              - den Auftrag durch Betätigung des eindeutig beschrifteten Buttons „kostenpflichtig bestellen“ bzw. „kostenpflichtig beauftragen“ absendet.
            </Typography>
            <Typography variant="body2">
              Die Auftragsannahme erfolgt durch Erklärung des AN gegenüber dem AG, insbesondere durch elektronische Bestätigung der Auftragsannahme.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§3 Gutachtenerstellung, Übersendung und Ausfertigungen</Typography>
            <Typography variant="body2">
              (1) Der AN erstellt zum o.g. Schadensereignis ein Schadensgutachten für den AG. Der AN ist verpflichtet, den erteilten Auftrag nach bestem Wissen
              und Gewissen auszuführen. Form, Gliederung, Formulierung und Inhalt des Gutachtens für Haftpflichtfälle entspricht dabei den Richtlinien des IFS.
              <br />
              (2) Der AG erhält das Gutachten im Original mit Lichtbildsatz postalisch. <br />
              (3) Eine weitere, dem Original mit Lichtbildsatz entsprechende, Ausfertigung des Gutachtens verbleibt bei dem AN zur ordnungsgemäßen Aufbewahrung
              und wird auf Verlangen des AG postalisch übersendet.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§4 Vergütung des Sachverständigen</Typography>
            <Typography variant="body2">
              (1) Das Sachverständigenhonorar des AN berechnet sich bei Schadengutachten auf Grundlage der Schadenhöhe und setzt sich aus einem Grundhonorar,
              Nebenkosten und Zusatzkosten gem. anliegender Honorartabelle zusammen.
              <br />
              (2) Für die Ausfertigung im Original und für die Ausfertigung zur ordnungsgemäßen Aufbewahrung durch den AN werden entsprechende Nebenkosten gem.
              der anliegenden Honorartabelle berechnet. Die Pauschale für Kommunikation fällt nur einmalig an.
              <br />
              (3) Werden im Rahmen der Besichtigung Kosten für Hilfsleistungen von Dritten erhoben, wie:
              <ul>
                <li>Kosten für die Demontage der Stoßfänger</li>
                <li>Kosten für die Nutzung einer Hebebühne oder</li>
                <li>Kosten für eine Achsvermessung</li>
              </ul>
              <br />
              werden diese Fremdkosten im Verhältnis zum Dritten zunächst vom AN getragen. Der AN und der AG vereinbaren, dass diese Kosten dem AN vom AG
              erstattet werden. Hierzu stellt der AN dem AG die durch ihn getragenen Fremdkosten in Rechnung.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§5 Mitwirkungspflichten des AG</Typography>
            <Typography variant="body2">
              Der AG hat dem AN alle zur ordnungsgemäßen Erstellung des Gutachtens erforderlichen Unterlagen und Auskünfte unentgeltlich und ohne besondere
              Aufforderung zur Verfügung zu stellen. Der AG hat insbesondere das Schadenausmaß und den Schadenumfang möglichst umfassend und wahrheitsgemäß zu
              erläutern, um eine ordnungsgemäße Schadenaufnahme zu ermöglichen. Alt- und Vorschäden sind vom AG zu benennen bzw. aufzuzeigen. Der AG sichert zu,
              dass er alle Angaben im Beauftragungsprozess wahrheitsgemäß beantwortet hat.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6" textAlign="center">
              Honorartabelle für Grundhonorar, Nebenkosten und Zusatzkosten
            </Typography>
            <Typography variant="body2">
              Sämtliche in der Honorartabelle aufgeführten €–Beträge für das Grundhonorar, Nebenkosten und Zusatzkosten werden jeweils als Nettowert und
              Bruttowert (inkl. {MEHRWERTSTEUER}% gesetzlicher Mehrwertsteuer) ausgewiesen.
            </Typography>
            <Grid size={12}>
              <Typography variant="h6">{'A) Grundhonorar'}</Typography>
              <Typography variant="body2">
                Als Schadenhöhe sind im Reparaturfall die ausgewiesenen Reparaturkosten netto gemäß Gutachten zzgl. einer eventuellen Wertminderung maßgebend.
                Bei einem Totalschaden (technischer Totalschaden oder wenn die Reparaturkosten brutto zuzüglich einer etwaigen merkantilen Wertminderung den
                Wiederbeschaffungswert übersteigen) ist der Wiederbeschaffungswert brutto des Fahrzeuges gemäß Gutachten unmittelbar vor dem Schadenereignis die
                Berechnungsgrundlage. Dabei ist immer die nächsthöhere „Schadenstufe“ („bis zu“) ausschlaggebend – und nicht der Wert, der „durchbrochen“ wurde.
              </Typography>
              <br />
              <br />
              <Grid container spacing={SPACING_BETWEEN_BOXES}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box component="table" sx={table}>
                    <thead>
                      <tr>
                        <th>Schadenhöhe netto bis</th>
                        <th>Grundhonorar netto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {firstHalf.map((entry, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <Euro value={(entry?.schadenhoehe ?? 0) / 100} />
                            </td>
                            <td>
                              <Euro value={(entry?.grundhonorar ?? 0) / 100} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box component="table" sx={table}>
                    <thead>
                      <tr>
                        <th>Schadenhöhe netto bis</th>
                        <th>Grundhonorar netto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {secondHalf.map((entry, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <Euro value={(entry?.schadenhoehe ?? 0) / 100} />
                            </td>
                            <td>
                              <Euro value={(entry?.grundhonorar ?? 0) / 100} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <br />
            <Grid container>
              <Grid container size={6} spacing={SPACING_BETWEEN_FORM_FIELDS}>
                <Grid size={12}>
                  <Typography variant="h6">{'B) Nebenkosten'}</Typography>
                </Grid>
                <Grid size={6}>
                  <b>Position</b>
                </Grid>
                <Grid size={6}>
                  <b>Nebenkosten netto</b>
                  <br />
                  (Nebenkosten brutto inkl. {MEHRWERTSTEUER}% Mehrwertsteuer)
                </Grid>
                <Grid size={6}>
                  Fotokosten für Original-Gutachten
                </Grid>
                <Grid size={6}>
                  2,00 € je Foto netto <br />
                  (2,38 € je Foto brutto)
                </Grid>
                <Grid size={6}>
                  Fotokosten für weitere Ausfertigungen
                </Grid>
                <Grid size={6}>
                  0,50 € je Foto netto <br />
                  (0,59 € je Foto brutto)
                </Grid>
                <Grid size={6}>
                  Schreibkosten für Original-Gutachten und Kopien
                </Grid>
                <Grid size={6}>
                  26,80 € pauschal netto
                  <br />
                  (31,89 € pauschal brutto)
                </Grid>
                <Grid size={6}>
                  Kommunikationspauschale
                </Grid>
                <Grid size={6}>
                  15,00 € pauschal netto <br />
                  (17,85 € pauschal brutto)
                </Grid>
              </Grid>
              <Grid container size={6} spacing={SPACING_BETWEEN_FORM_FIELDS}>
                <Grid size={12}>
                  <Typography variant="h6">{'C) Zusatzkosten'}</Typography>
                </Grid>
                <Grid size={6}>
                  <b>Position</b>
                </Grid>
                <Grid size={6}>
                  <b>Nebenkosten netto</b>
                  <br />
                  (Nebenkosten brutto inkl. {MEHRWERTSTEUER}% Mehrwertsteuer)
                </Grid>

                <Grid size={6}>
                  Fehlspeicher auslesen
                </Grid>
                <Grid size={6}>
                  70,00 € netto
                  <br />
                  (83,30 € brutto)
                </Grid>
                <Grid size={6}>
                  Lackschichtenmessung
                </Grid>
                <Grid size={6}>
                  70,00 € netto
                  <br />
                  (83,30 € brutto)
                </Grid>
                <Grid size={6}>
                  Restwertermittlung (je Abfrage)
                </Grid>
                <Grid size={6}>
                  24,00 € netto <br />
                  (28,56 € brutto)
                </Grid>
                <Grid size={6}>
                  EDV-Abfragegebühren
                </Grid>
                <Grid size={6}>
                  11,75 € netto
                  <br />
                  (13,98 € brutto)
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Signature
                onSign={() => {}}
                onDeleteSignature={() => {}}
                signatureUrl={url}
                isLoading={isLoading}
                unterzeichnerAuswahl={vorgang?.unterschriften?.auftragZurErstellungGutachten?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
                vorgang={vorgang}
                date={signDate}
                readOnly={true}
              />
            </Grid>
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
